import React, { useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { Field, FieldArray, FieldArrayRenderProps, FieldProps, Form, Formik, FormikState } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import {
  AlertContent,
  Button,
  ButtonIcon,
  ButtonSwitch,
  CentBasedInputCurrency,
  Checkbox,
  FieldWrapper,
  H3,
  H4,
  Icon,
  InputIconWrapper,
  Label,
  ModalFactory,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Select,
  Text,
  ValidationMessage,
} from 'components';
import LineItem from 'components/Calculator/ToggleableBreakdown/LineItem';
import { ISelectOption } from 'components/Select/types';
import { theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import { NumberInput } from 'screens/SiteSettings/TipCalculator/components/styles';
import { selectCalculatorRules } from 'store/CalculatorRules';
import { selectCalculatorPOSResults } from 'store/TipCalculator';
import { ICheck, IPOSTicketSplitOverride, IPOSTicketSplitOverrideShare, ISplitParticipant, RuleFundSource, TicketOverrideType } from 'types';
import { buildCheckSalesItems, buildSortedParticipantList, calculateDistribution, calculateEvenTicketSplitPercent, calculateTicketSplitTotal, centsToDollarsDisplay, forceRender } from 'utils';

const MAX_EMPLOYEES = 20;

interface ISplitCheckModalProps {
  checks: ICheck[];
  onSave(checks: ICheck[], overrides: IPOSTicketSplitOverride[]): void;
  onClose(): void;
}

const Layout = styled.section<{ $hidden?: boolean }>`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(680)};
  height: auto;
  margin: ${toRem(theme.layout.gutter.large)} 0;
  padding: ${toRem(theme.modal.spacing.large)};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;

      &:first-of-type + div {
        display: none;
      }
    `}

  label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(12)};
  }

  .equals {
    text-align: center;
    font-size: ${toRem(14)};
    margin-top: ${toRem(12)};
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(16)};
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(8)};
  width: 100%;
  height: auto;
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  justify-items: center;
  border: 1px solid ${theme.color.neutral[200]};
`;

const CheckDetailsWrapper = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(16)};
  justify-items: center;
  border: 1px solid ${theme.color.neutral[200]};
  height: auto;
  width: 100%;
  margin: ${toRem(16)} 0;
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
`;

const CheckDetailsHeader = styled.div`
  display: grid;
  grid-gap: ${toRem(8)};
  grid-template-columns: auto 2fr 2fr 2fr 2fr;
  align-items: center;
  width: 100%;
`;

const CheckDetails = styled.div`
  display: flex;
  padding: ${toRem(16)} 0;
  flex-direction: column;
  justify-content: center;
  gap: ${toRem(8)};
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.white};
  margin-top: ${toRem(16)};
  width: 100%;

  h4 {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.semiBold};
  }

  > :not(:last-child) {
    padding: 0 ${toRem(16)};
  }
`;

const ToggleHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-bottom: ${toRem(16)};
`;

const StyledButtonSwitch = styled(ButtonSwitch)`
  @media (max-width: 500px) {
    justify-content: space-between;
  }

  @media (max-width: 350px) {
    button {
      padding: ${toRem(4)} ${toRem(8)};
      font-size: ${toRem(10)};
    }
  }
`;

const LineItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: 0 0 ${toRem(16)} 0;
  overflow-x: auto;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${theme.color.neutral[300]};
  }

  > :not(:last-child) {
    padding: 0 ${toRem(16)};
  }

  div.total {
    p {
      font-weight: ${theme.font.weight.semiBold};
    }
  }
`;

const HorizontalScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${toRem(400)};
`;

const VerticalScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  overflow-y: auto;
`;

const LineItems = styled.div`
  display: flex;
  flex-direction: column;
  max-height: ${toRem(150)};
  padding: 0 ${toRem(16)};
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 1fr;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(8)};
  gap: ${toRem(8)};

  &.header-row {
    padding: 0 ${toRem(16)};
  }

  p.word-wrap {
    text-align: left;
    word-break: break-word; // Fallback for older browsers and IE
    overflow-wrap: anywhere;
    white-space: normal;
  }

  @media (max-width: ${theme.breakpoint.small}px) {
    gap: ${toRem(8)} ${toRem(4)};
  }
`;

const SalesHeaderRow = styled(ItemRow)`
  display: grid;
  grid-template-columns: 1fr 2fr;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: ${toRem(8)};

  p:last-child {
    text-align: right;
  }
`;

const Header = styled.p`
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(8)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;

  &:hover {
    cursor: pointer;
  }
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const RowItem = styled.div<{ $alignLeft?: boolean }>`
  display: flex;
  flex-flow: column;
  align-items: center;

  ${({ $alignLeft }) =>
    $alignLeft &&
    css`
      align-items: start;
    `}

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
    text-align: center;
    word-break: break-word;
    margin: auto;
  }

  & > label {
    // required to override existing specificity
    margin-bottom: ${toRem(8)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(11)};
  }

  & > h4 {
    // required to override existing specificity
    margin-bottom: ${toRem(12)};
    font-weight: ${theme.font.weight.bold};
  }

  & > h3 {
    font-weight: ${theme.font.weight.bold};
  }
`;

const SplitForm = styled.div<{ showRemove?: boolean }>`
  align-items: start;
  background-color: ${theme.color.neutral[50]};
  display: grid;
  grid-template-columns: 2fr ${toRem(120)} ${toRem(12)} ${toRem(120)};
  grid-gap: ${toRem(8)};
  padding: 0 ${toRem(16)};
  width: 100%;

  ${({ showRemove }) =>
    showRemove &&
    css`
      grid-template-columns: 2fr ${toRem(120)} ${toRem(12)} ${toRem(120)} ${toRem(20)};
    `}

  @media (max-width: ${theme.breakpoint.small}px) {
    grid-template-columns: 2fr ${toRem(80)} ${toRem(12)} ${toRem(80)};

    ${({ showRemove }) =>
      showRemove &&
      css`
        grid-template-columns: 2fr ${toRem(80)} ${toRem(12)} ${toRem(80)} ${toRem(20)};
      `}
  }
`;

const Remaining = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-self: end;
  align-items: start;
  gap: ${toRem(12)};
  margin: ${toRem(12)} 0;
`;

const RemainingRow = styled.div<{ showRemove?: boolean }>`
  display: grid;
  align-items: center;
  grid-gap: ${toRem(22)};
  grid-template-columns: 2fr ${toRem(124)};

  ${({ showRemove }) =>
    showRemove &&
    css`
      grid-template-columns: 2fr ${toRem(152)};
    `}

  @media (max-width: ${theme.breakpoint.small}px) {
    grid-template-columns: 2fr ${toRem(85)};

    ${({ showRemove }) =>
      showRemove &&
      css`
        grid-template-columns: 2fr ${toRem(112)};
      `}
  }

  p {
    font-size: ${toRem(14)};
  }
`;

const Controls = styled.div`
  display: grid;
  background-color: ${theme.color.neutral[50]};
  grid-template-columns: 1fr 1fr;
  padding: 0 ${toRem(16)} ${toRem(16)} ${toRem(16)};
  width: 100%;
  border-radius: ${toRem(11)};

  > * {
    &:first-child {
      justify-self: start;
    }

    &:last-child {
      justify-self: end;
    }
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledIconButton = styled(ButtonIcon)`
  width: ${toRem(24)} !important;
  height: ${toRem(24)} !important;
  margin-top: ${toRem(12)};

  svg {
    fill: ${theme.color.neutral[500]};
  }

  &:focus {
    border-color: ${theme.color.neutral[400]};
  }

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${theme.color.neutral[100]} !important;
    `}
`;

const StyledFieldWrapper = styled(FieldWrapper)`
  align-items: center;
  margin-bottom: 0 !important;

  section {
    margin-top: ${toRem(4)};

    p {
      font-size: ${toRem(11)};
    }
  }
`;

const SubTitle = styled(Text)`
  font-size: ${toRem(14)};
  font-weight: ${theme.font.weight.medium};
  width: 100%;
  padding: ${toRem(4)} ${toRem(16)};
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: ${toRem(12)};
  padding: ${toRem(12)} ${toRem(16)};

  input {
    margin-right: ${toRem(8)};
  }

  label {
    font-size: ${toRem(14)};
    color: ${theme.color.neutral[900]};
  }
`;

const ErrorMessage = styled(ValidationMessage)`
  font-size: ${toRem(11)};
`;

type SplitTypes = 'shares' | 'tips' | 'autograts';

interface IFormSplitOverride extends IPOSTicketSplitOverride {
  tips?: IPOSTicketSplitOverrideShare[];
  autograts?: IPOSTicketSplitOverrideShare[];
}

const buildDefaultShare = (sources: RuleFundSource[] = [RuleFundSource.TIPS, RuleFundSource.AUTO_GRATUITIES]): IPOSTicketSplitOverrideShare => ({
  participant: { id: undefined, name: undefined, type: undefined },
  gross: 0,
  tip: 0,
  autoGratuity: 0,
  percent: 0,
  sources,
});

const calculateRemainder = (shares: IPOSTicketSplitOverrideShare[], total: number) => {
  const sumAmount = shares.reduce((acc, val) => acc + Number(val.gross || 0), 0);
  const remainingAmount = total - sumAmount;
  return remainingAmount;
};

export const SplitCheckModal: React.FC<ISplitCheckModalProps> = ({ checks, onClose, onSave }) => {
  const posDataResults = useSelector(selectCalculatorPOSResults());
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const [isDirtyCancel, setIsDirtyCancel] = useState(false);
  const [showRemainderError, setShowRemainderError] = useState(false);
  const [separateSplit, setSeparateSplit] = useState(false);
  const [open, setOpen] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false); // false === checks, true === sales
  const onSwitchChange = () => setSwitchChecked(!switchChecked);

  const setInitialValues = (separateSplit: boolean): IFormSplitOverride => ({
    type: TicketOverrideType.SPLIT,
    shares: separateSplit ? [] : [buildDefaultShare()],
    ...(separateSplit && {
      tips: [buildDefaultShare([RuleFundSource.TIPS])],
      autograts: [buildDefaultShare([RuleFundSource.AUTO_GRATUITIES])],
    }),
  });

  const shareValidation = Yup.array().of(
    Yup.object().shape({
      participant: Yup.object()
        .shape({
          id: Yup.string().required('Please select a recipient'),
          name: Yup.string().required(),
          type: Yup.string().required(),
        })
        .required(),
      percent: Yup.number().required('Must be more than 0%').moreThan(0, 'Must be more than 0%').max(100, 'Cannot exceed 100%'),
      gross: Yup.number(),
    })
  );

  const validationSchema = Yup.object().shape({
    type: Yup.string().required('Type is required'),
    shares: shareValidation,
    tips: shareValidation,
    autograts: shareValidation,
  });

  const participantSelectorOptions = useMemo(
    () =>
      buildSortedParticipantList(posDataResults, currentCalculatorRules?.participants).map((participant) => ({
        value: participant,
        label: participant.name,
      })),
    [posDataResults, currentCalculatorRules]
  );

  const totals = useMemo<Record<SplitTypes, number>>(
    () => ({
      tips: checks.reduce((sum, total) => sum + total.totals.tip.gross, 0),
      autograts: checks.reduce((sum, total) => sum + total.totals.autoGratuity.gross, 0),
      shares: checks.reduce((sum, total) => sum + total.totals.gross, 0),
    }),
    [checks]
  );

  const handleRemoveShare = (sharesArrayHelpers: FieldArrayRenderProps, shareIndex: number) => {
    sharesArrayHelpers.remove(shareIndex);
  };

  const getRemainder = (values: IFormSplitOverride) => {
    const remainder = { tips: totals.tips, autograts: totals.autograts, total: totals.shares };
    if (!separateSplit) {
      remainder.total = calculateRemainder(values.shares, remainder.total);
    } else {
      remainder.tips = calculateRemainder(values.tips, remainder.tips);
      remainder.autograts = calculateRemainder(values.autograts, remainder.autograts);
      remainder.total = remainder.tips + remainder.autograts;
    }
    return remainder;
  };

  const handleSubmit = async (values: IFormSplitOverride) => {
    if (getRemainder(values).total !== 0) {
      setShowRemainderError(true);
    } else {
      await forceRender();

      const calculateGross = checks.length > 1; // if there is only 1 check, we can reuse gross values

      const overrides = checks.map((check) => {
        let ticketShareOverride: IPOSTicketSplitOverrideShare[];
        if (!separateSplit) {
          // tips & autograts
          ticketShareOverride = calculateTicketSplitTotal(values.shares, check, calculateGross);
        } else {
          const tips = calculateTicketSplitTotal(values.tips, check, calculateGross);
          const autograts = calculateTicketSplitTotal(values.autograts, check, calculateGross);
          ticketShareOverride = [...tips, ...autograts];
        }
        return { type: values.type, shares: ticketShareOverride };
      });
      onSave(checks, overrides);
    }
  };

  const validatePercent = (value: string) => {
    const pattern = /^\d{1,3}(\.(\d{1,2})?)?$/i;
    return value === '' || pattern.test(value);
  };

  const calculateAmount = (percent: string, key: SplitTypes) => {
    if (!percent) return '';
    const amount = (Number(percent) * totals[key]) / 100;
    return `${Math.round(amount)}`;
  };

  const calculatePercent = (amount: string, key: SplitTypes) => {
    if (!amount) return '';
    const percent = (Number(amount) * 100) / totals[key];
    if (percent > 999.99) return '999.99';

    return `${Math.round(percent * 100) / 100}`; // rounded to 2 decimals
  };

  const handleSeparateSplit = (checked: boolean, resetForm: (nextState?: Partial<FormikState<IFormSplitOverride>>) => void) => {
    resetForm({ values: setInitialValues(checked) });
    setSeparateSplit(checked);
    setShowRemainderError(false);
  };

  const handleAddParticipant = (arrayHelper: FieldArrayRenderProps, key: SplitTypes) => {
    const sources = key === 'tips' ? [RuleFundSource.TIPS] : key === 'autograts' ? [RuleFundSource.AUTO_GRATUITIES] : [RuleFundSource.TIPS, RuleFundSource.AUTO_GRATUITIES];
    arrayHelper.push(buildDefaultShare(sources));
  };

  const handleSplitEvenly = (arrayHelper: FieldArrayRenderProps, key: SplitTypes) => {
    // Calculate the percentages for the split evenly
    calculateEvenTicketSplitPercent(arrayHelper.form.values[key], totals[key]).forEach((item, index) => {
      arrayHelper.replace(index, item);
    });
  };

  const handleCancel = (dirty: boolean) => {
    setIsDirtyCancel(dirty);
    if (dirty) {
      showCancelModal();
    } else {
      onClose();
    }
  };

  // Build Sales
  const salesLineItems: ITipsBreakdownLineItem[] = useMemo(
    () =>
      buildCheckSalesItems(
        checks.flatMap((check) => check.categories),
        currentCalculatorRules?.categoryGroups
      ),
    [checks, currentCalculatorRules]
  );

  const [showCancelModal, hideCancelModal] = useModal(
    () => (
      <ModalFactory
        customModal
        onClose={() => {
          setIsDirtyCancel(false);
          hideCancelModal();
        }}
        isActive
        closeOnOutSideClick={false}
        hideCloseButton
      >
        <AlertContent
          title="Cancel Changes?"
          message="Do you want to cancel the changes you've made to these checks?"
          leftActionLabel="Go Back"
          rightActionLabel="Cancel Changes"
          onLeftAction={() => {
            setIsDirtyCancel(false);
            hideCancelModal();
          }}
          onRightAction={() => {
            hideCancelModal();
            onClose();
          }}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    ),
    [onClose]
  );

  const sharesArray = (key: SplitTypes, values: IFormSplitOverride, remainder: number, setFieldValue: (field: string, value: any) => void, setFieldTouched: (field: string) => void) => (
    <FieldArray
      name={key}
      render={(sharesArrayHelpers) => (
        <>
          <SubTitle>{key === 'tips' ? 'Tips' : key === 'autograts' ? 'Autograts' : 'Tips & Autograts'}</SubTitle>
          <SplitForm data-cy={`split-check-${key}-form`} showRemove={values[key].length > 1}>
            <Label>Recipient</Label>
            <Label>Split %</Label>
            <Label />
            <Label>Split $</Label>
            {values[key].length > 1 && <Label />}
            {values[key].map((share, sharesIndex) => (
              <React.Fragment key={`${key}_${share.participant.id || sharesIndex}`}>
                <Field name={`${key}.${sharesIndex}.participant.id`}>
                  {({ field, meta }: FieldProps) => (
                    <StyledFieldWrapper message={meta.error && meta.touched ? meta.error : ''} hasError={meta.error && meta.touched}>
                      <Select
                        {...field}
                        data-cy="check-split-participant-select"
                        values={field.value ? [participantSelectorOptions.find((opt) => opt.value.id === field.value)] : []}
                        options={participantSelectorOptions.filter((opt) => !values[key].some((item) => item.participant.id === opt.value.id) || opt.value.id === field.value)}
                        onChange={(selected: ISelectOption<ISplitParticipant>[]) => {
                          if (!meta.touched) setFieldTouched(`${key}.${sharesIndex}.participant.id`);
                          setFieldValue(`${key}.${sharesIndex}.participant`, selected[0]?.value);
                        }}
                        valueField="value.id"
                        error={meta.error && meta.touched}
                        placeholder="Select Recipient"
                        id={`${key}-participant-selector-${sharesIndex}`}
                        className="selector"
                        searchable
                      />
                    </StyledFieldWrapper>
                  )}
                </Field>
                <Field name={`${key}.${sharesIndex}.percent`}>
                  {({ field, meta }: FieldProps) => (
                    <StyledFieldWrapper message={meta.error && meta.touched ? meta.error : ''} hasError={meta.error && meta.touched}>
                      <InputIconWrapper icon="Percentage" iconPosition="right">
                        <NumberInput
                          {...field}
                          inputMode="decimal"
                          onChange={(e) => {
                            const percent = e.target.value;
                            if (!meta.touched) {
                              setFieldTouched(`${key}.${sharesIndex}.gross`);
                              setFieldTouched(`${key}.${sharesIndex}.percent`);
                            }
                            if (!validatePercent(percent)) return;

                            let totalPercent = 0;
                            const percents = values[key].map((share, i) => {
                              const value = Number(i === sharesIndex ? percent : share.percent) || 0;
                              totalPercent += value;
                              return value;
                            });

                            if (Math.round(totalPercent * 100) / 100 === 100) {
                              // if percents add up to 100%, we recalculate all gross amounts to make sure there is no remainder left
                              const grossDistribution = calculateDistribution(percents, totals[key]);

                              setFieldValue(
                                key,
                                values[key].map((share, i) => ({
                                  ...share,
                                  percent: percents[i],
                                  appliedPercent: percents[i],
                                  gross: grossDistribution[i],
                                }))
                              );
                            } else {
                              // if not, only the current row gets updated
                              setFieldValue(`${key}.${sharesIndex}`, {
                                ...share,
                                percent,
                                appliedPercent: percent,
                                gross: calculateAmount(percent, key),
                              });
                            }
                          }}
                          value={field.value || ''}
                          $error={meta.error && meta.touched}
                          data-cy="split-percentage-input"
                          id={`${key}-percentage-input-${sharesIndex}`}
                          placeholder="00.00"
                        />
                      </InputIconWrapper>
                    </StyledFieldWrapper>
                  )}
                </Field>
                <Text className="equals">=</Text>
                <Field name={`${key}.${sharesIndex}.gross`}>
                  {({ field, meta }: FieldProps) => (
                    <StyledFieldWrapper message={meta.error && meta.touched ? meta.error : ''} hasError={meta.error && meta.touched}>
                      <CentBasedInputCurrency
                        {...field}
                        data-cy="split-amount-input"
                        value={field.value || ''}
                        onChange={(value) => {
                          if (!meta.touched) {
                            setFieldTouched(`${key}.${sharesIndex}.gross`);
                            setFieldTouched(`${key}.${sharesIndex}.percent`);
                          }
                          const percent = calculatePercent(value, key);
                          setFieldValue(`${key}.${sharesIndex}`, {
                            ...share,
                            gross: value,
                            percent,
                            appliedPercent: percent,
                          });
                        }}
                        hasError={meta.error && meta.touched}
                        placeholder="$00.00"
                        allowZero
                      />
                    </StyledFieldWrapper>
                  )}
                </Field>
                {values[key].length > 1 && <StyledIconButton label="Remove" onClick={() => handleRemoveShare(sharesArrayHelpers, sharesIndex)} appearance="white" size="small" icon="Close" data-cy="remove-share-button" />}
              </React.Fragment>
            ))}
          </SplitForm>

          <Remaining>
            <RemainingRow showRemove={values[key].length > 1}>
              <Label>Remaining</Label>
              <Text>{remainder ? centsToDollarsDisplay(remainder) : '$00.00'}</Text>
            </RemainingRow>

            {showRemainderError && remainder !== 0 && (
              <ErrorMessage data-cy="remainder-error" hasError>
                Remaining must equal $0.00
              </ErrorMessage>
            )}
          </Remaining>

          <Controls data-cy="split-check-modal-form-controls">
            <Button data-cy="add-recipient-btn" label="Add Recipient" appearance="white" icon="Plus" size="small" type="button" onClick={() => handleAddParticipant(sharesArrayHelpers, key)} disabled={values[key].length >= MAX_EMPLOYEES} />
            <Button data-cy="split-evenly-btn" label="Split Evenly" appearance="white" icon="Split" size="small" type="button" onClick={() => handleSplitEvenly(sharesArrayHelpers, key)} />
          </Controls>
        </>
      )}
    />
  );

  return (
    <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false} hideCloseButton>
      <Layout $hidden={isDirtyCancel} data-cy="split-check-modal">
        <Formik initialValues={setInitialValues(separateSplit)} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ dirty, isSubmitting, values, resetForm, setFieldTouched, setFieldValue }) => {
            const remainder = getRemainder(values);
            return (
              <Form>
                <ModalHeader data-cy="split-check-modal-header">
                  <ModalTitle>Split Checks</ModalTitle>
                </ModalHeader>
                <CheckDetailsWrapper data-cy="split-check-modal-check-details-wrapper">
                  <CheckDetailsHeader data-cy="split-check-modal-check-details-header">
                    <DropdownIconWrapper
                      data-cy="split-check-modal-accordion-trigger"
                      onClick={() => {
                        setOpen(!open);
                      }}
                    >
                      <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
                    </DropdownIconWrapper>
                    <RowItem data-cy="check-count" $alignLeft>
                      <H3>Checks ({checks.length})</H3>
                    </RowItem>
                    <RowItem data-cy="tips">
                      <Label>Tips</Label>
                      <Text>{centsToDollarsDisplay(totals.tips)}</Text>
                    </RowItem>
                    <RowItem data-cy="autograts">
                      <Label>Autograts</Label>
                      <Text>{centsToDollarsDisplay(totals.autograts)}</Text>
                    </RowItem>
                    <RowItem data-cy="total">
                      <Label>Tips & Autograts</Label>
                      <Text>{centsToDollarsDisplay(totals.shares)}</Text>
                    </RowItem>
                  </CheckDetailsHeader>
                  {open && (
                    <CheckDetails>
                      <ToggleHeader>
                        <H4 fontWeight="medium">{!switchChecked ? 'Check Details' : 'Sales'}</H4>
                        <StyledButtonSwitch dataCy="split-check-modal-switch" onChange={onSwitchChange} checked={switchChecked} leftText="Checks" rightText="Sales" data-cy="toggleable-breakdown-switch" />
                      </ToggleHeader>
                      {switchChecked ? (
                        <LineItemsSection>
                          <SalesHeaderRow>
                            <Header>Sales</Header>
                            <Header>Amount</Header>
                          </SalesHeaderRow>
                          <VerticalScrollContainer>
                            <LineItems>
                              {salesLineItems?.map((lineItem, idx) => (
                                <LineItem key={`line_item_sales_${idx}`} item={lineItem} />
                              ))}
                            </LineItems>
                          </VerticalScrollContainer>
                        </LineItemsSection>
                      ) : (
                        <LineItemsSection>
                          <HorizontalScrollContainer>
                            <ItemRow className="header-row">
                              <Header>Check</Header>
                              <Header>Employee</Header>
                              <Header>Tips</Header>
                              <Header>Autograts</Header>
                              <Header>Total</Header>
                            </ItemRow>
                            <VerticalScrollContainer>
                              <LineItems>
                                {checks.map((check) => (
                                  <ItemRow key={`check-${check.ticketId}`}>
                                    <Text size="xsmall">#{check.displayId}</Text>
                                    <Text className="word-wrap" size="xsmall">
                                      {check.employeeName}
                                    </Text>
                                    <Text size="xsmall">{centsToDollarsDisplay(check.totals.tip.gross)}</Text>
                                    <Text size="xsmall">{centsToDollarsDisplay(check.totals.autoGratuity.gross)}</Text>
                                    <Text size="xsmall">{centsToDollarsDisplay(check.totals.gross)}</Text>
                                  </ItemRow>
                                ))}
                              </LineItems>
                            </VerticalScrollContainer>
                          </HorizontalScrollContainer>
                        </LineItemsSection>
                      )}
                    </CheckDetails>
                  )}
                </CheckDetailsWrapper>
                <ModalBody>
                  <StyledCheckbox
                    id="separateSplit"
                    label="Separate Tips & Autograts"
                    size="small"
                    checked={separateSplit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSeparateSplit(e.target.checked, resetForm)}
                    disabled={!totals.tips || !totals.autograts}
                  />
                  {!separateSplit ? (
                    sharesArray('shares', values, remainder.total, setFieldValue, setFieldTouched)
                  ) : (
                    <>
                      {sharesArray('tips', values, remainder.tips, setFieldValue, setFieldTouched)}
                      {sharesArray('autograts', values, remainder.autograts, setFieldValue, setFieldTouched)}
                    </>
                  )}
                </ModalBody>
                <ModalFooter columns={2} data-cy="split-check-modal-footer">
                  <StyledButton data-cy="cancel-btn" label="Cancel" appearance="white" size="large" onClick={() => handleCancel(dirty)} disabled={isSubmitting} />
                  <StyledButton data-cy="save-btn" label="Save" type="submit" isProcessing={isSubmitting} disabled={(showRemainderError && remainder.total !== 0) || isSubmitting} appearance="primary" size="large" />
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Layout>
    </ModalFactory>
  );
};
